import React, {useState} from 'react';
import {useForm} from "react-hook-form";
import Button from "@material-ui/core/Button";
import {Box, createStyles, TextField, Theme, Typography, MenuItem} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import {Invitation, Role} from "myfitworld-model";
import {useOrganizationProvider} from "../../providers/OrganizationProvider";
import {useUserProvider} from "../../providers/UserProvider";
import firebase from "firebase";
import {makeStyles} from "@material-ui/core/styles";
import {validatePhoneNumber} from "../../validators/validatePhoneNumber";
import {validateEmail} from "../../validators/validateEmail";
import {sendInvitation} from "../../api/invitationsApi";
import LoadingSpinner from '../LoadingSpinner';
import {checkEmailStatus, getUserByEmail, updateInvitedUser} from '../../api/usersApi';
import globalState, { GlobalState } from '../../globalState';
import { useMfwThemeProvider } from 'myfitworld-utils';
import globalMessages from '../../messages';
import invitationMessages from './messages';
import {useIntl} from 'react-intl';
import { Link, Check } from '@material-ui/icons';
import StripePricingDialogInfo from "../dialogs/stripePopupModals/StripePricingDialogInfo";
import useBillingDetails from 'src/hooks/useBillingDetails';
import { billingOptionsByPriceId } from 'src/utils/billing/billing';
import { format } from 'date-fns';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiTextField-root': {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(3)
      },
    },
  }),
);

const InviteUserForm = ({onSubmit, roles, createActionLabel, invitationsBlocked, submitDisabled, onSubmitDisabledClick, submitDisabledMessage}: Props) => {

  const {register, errors, handleSubmit, reset} = useForm();
  const [loading, setLoading] = useState(false);
  const [disabledForm, setDisabledForm] = useState(false);
  const [emailError, setEmailError ] = useState<number>(4);
  const [role, setRole] = useState('');
  const [roleError, setRoleError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openStripePricingDialog, setStripePricingDialog] = useState(false);

  const classes = useStyles();
  const {formatMessage} = useIntl();
  const [{selectedOrganization}] = useOrganizationProvider();
  const {user} = useUserProvider();
  const theme = useMfwThemeProvider();
  const { billingDetails } = useBillingDetails(selectedOrganization?.id);

  const onSubmitHandler = async (data: any) => {
    if(role === '' && roles.length >1){
      setRoleError(true);
      return;
    }
    //check email/phone if they are taken
    setLoading(true);
    setDisabledForm(true);

    const dbStatus = await checkEmailStatus(
      data.emailOrPhone.trim().toLowerCase(),
      user?.currentOrganization!!,
      theme.theme.orgId
    );

    if(dbStatus !== 4){
      setLoading(false);
      setDisabledForm(false);
      setEmailError(dbStatus);
      return;
    }

    const invitedUser = await getUserByEmail(data.emailOrPhone.trim().toLowerCase(), theme.theme.orgId);

    sendInvitation({
      firstName: invitedUser?.firstName,
      lastName: invitedUser?.lastName,
      email: validateEmail(data.emailOrPhone.trim().toLowerCase()) === true ? data.emailOrPhone.trim().toLowerCase() : null,
      phoneNumber: validatePhoneNumber(data.emailOrPhone.trim()) === true ? data.emailOrPhone.trim() : null,
      organization: roles[0] === Role.SuperAdmin ? null : selectedOrganization?.id || null,
      role: roles.length > 1 ? role : roles[0],
      createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
      invitedBy: user?.id,
      invitationSent: false,
      isInSendingQueue: true,
      acceptedOn: null,
      archived:false,
      rejected: false,
      userId: invitedUser?.id,
      userExist: true
    } as unknown as Invitation, roles.includes(Role.Client), selectedOrganization?.id ||"" ).then((result:boolean)=>{
      if(result){
        setLoading(false);
        setDisabledForm(false);
        updateInvitedUser(data.emailOrPhone.trim().toLowerCase(), theme.theme.orgId);
        onSubmit();
        globalState.update((state: GlobalState) => {
          state.toastQueue.push({message: formatMessage(invitationMessages.the_invitation_has_been_sent_to_the_email) + " " + data.emailOrPhone + ".", severity: "success"});
        });
        reset();
        setRole("");
      }
    });
  };

  const getEmailErrorTextMessage = () => {
    if (errors.emailOrPhone) {
      return errors.emailOrPhone?.type === 'required' ? formatMessage(globalMessages.required_field) : formatMessage(invitationMessages.invalid_email);
    } else {
      if (emailError === 0) {
        return formatMessage(roles.length > 1 ? invitationMessages.this_email_is_not_exist_web : invitationMessages.this_email_is_not_exist, {name: theme.theme.whitelabel.name});
      } else if (emailError === 1) {
        return formatMessage(invitationMessages.this_email_is_already_connected_to_another_organization);
      } else if (emailError === 2) {
        return formatMessage(invitationMessages.this_email_is_already_connected_to_your_organization);
      } else if (emailError === 3) {
        return formatMessage(invitationMessages.this_email_is_already_invited_from_your_organization);
      } else if (emailError === 5) {
        return formatMessage(invitationMessages.client_account);
      } else if (emailError === 6) {
        return formatMessage(invitationMessages.this_email_is_not_client);
      } else {
        return null;
      }
    }
  }

  const createCopyLink = () => {
    const link = `${theme.theme.domain}/registration/${role.toLowerCase()}/${user?.currentOrganization}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
  }

  const handleHereClick = () => {
    setStripePricingDialog(true);
  }

  const closeStripePricingDialog = () => {
    setStripePricingDialog(false);
  };

  const selectedPriceId = billingDetails?.rebrandSubscription?.priceId
    || billingDetails?.stripe?.priceId || undefined;

  const currentBillingOption = selectedPriceId ? billingOptionsByPriceId[selectedPriceId] : undefined;

  const cancelDateTime = currentBillingOption?.id.toLowerCase().includes('rebrand') ?
    billingDetails?.rebrandSubscription?.cancelAt ? new Date(+billingDetails?.rebrandSubscription?.cancelAt*1000) : undefined
    : billingDetails?.stripe?.cancelAt ? new Date(+billingDetails?.stripe?.cancelAt*1000) : undefined;

  const renewalDate =  billingDetails?.rebrandSubscription?.subscriptionRenewalDate ? new Date(+billingDetails?.rebrandSubscription?.subscriptionRenewalDate*1000) :
    billingDetails?.stripe?.subscriptionRenewalDate ? new Date(+billingDetails?.stripe?.subscriptionRenewalDate*1000) : undefined;


  return (
    <form onSubmit={onSubmitHandler} className={classes.root}>
      {roles.length > 1 &&
        <TextField
          select
          fullWidth
          label={formatMessage(globalMessages.role)}
          value={role !== '' ? formatMessage((invitationMessages as any)[role.toLowerCase()]): ''}
          error={roleError}
          disabled={disabledForm || submitDisabled}
          helperText={roleError ? formatMessage(globalMessages.required_field) : null}
        >
          {
            roles.map((role) => (
              <MenuItem key={role} value={formatMessage((invitationMessages as any)[role.toLowerCase()])} onClick={() => {setRole(role); setCopied(false); setRoleError(false);}}>
                {formatMessage((invitationMessages as any)[role.toLowerCase()])}
              </MenuItem>
            ))
          }
        </TextField>
      }
      <Box marginTop={createActionLabel === 'create_member' ? 3 : 0}>
        {createActionLabel === 'create_member' && !theme.theme.isWhiteLabel && <Typography style={{marginBottom: -20}}>{formatMessage(invitationMessages.have_account)}</Typography>}
        {((createActionLabel === 'add_client' && theme.theme.isWhiteLabel) || (!theme.theme.isWhiteLabel))&&
          <TextField
            name="emailOrPhone"
            inputRef={register({
              required: true,
              pattern: {
                value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: formatMessage(invitationMessages.invalid_email)
              }
            })}
            error={!!errors.emailOrPhone || emailError !== 4}
            helperText={getEmailErrorTextMessage()}
            label={formatMessage(globalMessages.email)}
            fullWidth
            disabled={disabledForm || submitDisabled}
            autoComplete='off'
            onChange={() => setEmailError(4)}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSubmit(onSubmitHandler)();
              }
            }}
          />
        }
      </Box>

      {!loading && (!theme.theme.isWhiteLabel || (theme.theme.isWhiteLabel && createActionLabel === 'add_client')) &&
        <Box
          onClick={()=>{setRoleError(role==='')}}
          display='flex'
          justifyContent='center'
          marginTop={1}
          marginBottom={createActionLabel === 'add_client' ? 2 : 0}
        >
          <Button
            color='primary'
            variant='contained'
            onClick={submitDisabled ? onSubmitDisabledClick : handleSubmit(onSubmitHandler)}
            disabled={loading || invitationsBlocked || (submitDisabled && !onSubmitDisabledClick)}
          >
            {submitDisabled ? formatMessage(invitationMessages.manage_subscriptions) : formatMessage((invitationMessages as any)[createActionLabel])}
          </Button>
        </Box>
      }
      {createActionLabel === 'create_member' &&
        <Box marginTop={5}>
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              {formatMessage(invitationMessages.doesnt_have_account_title)}
            </Typography>
            <Typography paragraph>
              {formatMessage(invitationMessages.doesnt_have_account_step_one)}
            </Typography>
            <Typography paragraph>
              {formatMessage(invitationMessages.doesnt_have_account_step_two)}<br/>
              {formatMessage(invitationMessages.doesnt_have_account_text)}<br/>
              {formatMessage(invitationMessages.doesnt_have_account_note)}
            </Typography>
          </Box>
          <Box
            display='flex'
            justifyContent={theme.theme.isWhiteLabel ? 'flex-end' : 'center'}
            marginTop={3}
          >
            <Button
              color='primary'
              variant='outlined'
              onClick={() => !copied && createCopyLink()}
              disabled={role===''}
              startIcon={copied ? <Check style={{fill: theme.mfwMobTheme.primary}}/> : <Link style={{fill: role === "" ? theme.mfwMobTheme.primaryLight : theme.mfwMobTheme.primary}}/>}
            >
              {copied ? formatMessage(invitationMessages.copied_link) : formatMessage(invitationMessages.copy_link)}
            </Button>
          </Box>
        </Box>
      }

      {loading &&
        <Box style = {{margin:"20px"}}>
          <LoadingSpinner marginTop={1}/>
          <Typography variant='h5' style={{textAlign:'center', color: theme.mfwMuiWebTheme.palette.primary.main}}>{formatMessage(invitationMessages.sending_connection_request)}</Typography>
        </Box>
      }

      {submitDisabled && submitDisabledMessage &&
        <Box mt={1}>
          <Alert severity="info" style={{marginBottom:"10px"}}>{submitDisabledMessage}</Alert>
        </Box>
      }

      {currentBillingOption?.clients && !theme.theme.isWhiteLabel && !selectedOrganization?.free &&
        <Alert severity="info" style={{marginBottom:"10px"}}>
          <span>REBRAND BUNDLE</span><br/>
          <span>{`${formatMessage(globalMessages.clients)}: ${currentBillingOption.clients}`}</span><br/>
          <span>{`${formatMessage(globalMessages.billing_info_period)}: ${currentBillingOption.period}`}</span><br/>
          {cancelDateTime && <><span>{`${formatMessage(globalMessages.expires)}: ${cancelDateTime}`}</span><br/></>}
          {renewalDate && <><span>{`${formatMessage(globalMessages.next_payment)}: ${format(renewalDate, "dd/MM/yyyy")}`}</span><br/></>}
          <span>{formatMessage(globalMessages.high_subscription_plan_info)}</span><br/>
        </Alert>
      }

      {roles.length === 1 && !theme.theme.isWhiteLabel && !selectedOrganization?.free && (
        <Alert severity="info" style={{marginBottom:"10px"}}>
          <Typography color="textPrimary">
            {formatMessage(invitationMessages.create_client_help_text_bold_3)}{" "}
            <Typography component="span" color="primary" style={{ cursor: 'pointer', fontWeight: 600 }} onClick={handleHereClick}>
              {formatMessage(invitationMessages.create_client_help_text_bold_3_here)}
            </Typography>
          </Typography>
        </Alert>
      )}

      {roles.length === 1 &&
        <Box>
          <Alert severity="info" style={{marginBottom:"10px"}}>
            <span>{formatMessage(invitationMessages.install_mob_app_first, {name: theme.theme.whitelabel.name})}</span><br></br>
          </Alert>
          <Alert severity="info" style={{marginBottom:"10px"}}>
            <span>{formatMessage(invitationMessages.create_client_help_text)}</span><br></br>
          </Alert>
        </Box>
      }
      
      {roles.length === 1 && !theme.theme.isWhiteLabel && !selectedOrganization?.free &&
        <Alert severity="info" style={{marginBottom:"10px"}}>    
          <span style={{fontWeight: "bold"}}>{formatMessage(invitationMessages.create_client_help_text_bold)}</span>
        </Alert>
      }
      
      {roles.length === 1 && !theme.theme.isWhiteLabel && !selectedOrganization?.free &&
        <Alert severity="info" style={{marginBottom:"10px"}}>
          <span style={{fontWeight: "bold"}}>{formatMessage(invitationMessages.create_client_help_text_bold_2)}</span>
        </Alert>
      }

      {openStripePricingDialog && <StripePricingDialogInfo open={openStripePricingDialog} onClose={closeStripePricingDialog}/>}

      {invitationsBlocked &&
        <Box mt={1}>
          <Alert severity="warning">
            {formatMessage(invitationMessages.invitation_disabled)}
          </Alert>
        </Box>
      }
    </form>
  )
}

export default InviteUserForm;

interface Props {
  roles: Role[];
  onSubmit: () => void;
  createActionLabel: string;
  helperText?: string;
  invitationsBlocked?: boolean;
  submitDisabled?: boolean;
  onSubmitDisabledClick?: () => void;
  submitDisabledMessage?: string;
}
